// session/normalizers.ts — Normalizer functions for session sub-objects

import type {
    AccessibilitySettings,
    EnabledModules,
    NotificationPreferences,
    SessionState,
} from '@shared/types/domain-types'
import { getCurrentSeasonId } from '@/lib/config/governance'
import { DEFAULT_STATE } from './types'

export function normalizeSessionState(input?: Partial<SessionState> | null): SessionState {
    const base = input ?? {}
    const currentYear = new Date().getFullYear()
    const currentSeasonId = getCurrentSeasonId()

    return {
        ...DEFAULT_STATE,
        ...base,
        network: {
            ...DEFAULT_STATE.network,
            ...(base.network ?? {}),
        },
        telemetry: {
            ...DEFAULT_STATE.telemetry,
            ...(base.telemetry ?? {}),
        },
        accessibility: {
            ...DEFAULT_STATE.accessibility,
            ...(base.accessibility ?? {}),
        },
        arsenal: {
            ...DEFAULT_STATE.arsenal,
            ...(base.arsenal ?? {}),
            hud: {
                ...DEFAULT_STATE.arsenal.hud,
                ...(base.arsenal?.hud ?? {}),
            },
        },
        cycle: {
            ...DEFAULT_STATE.cycle,
            ...(base.cycle ?? {}),
            lastSeenYear: base.cycle?.lastSeenYear ?? currentYear,
            lastSeenSeasonId: base.cycle?.lastSeenSeasonId ?? currentSeasonId,
        },
        offlineQueue: {
            ...DEFAULT_STATE.offlineQueue,
            ...(base.offlineQueue ?? {}),
            items: base.offlineQueue?.items ?? DEFAULT_STATE.offlineQueue.items,
        },
        idempotency: {
            ...DEFAULT_STATE.idempotency,
            ...(base.idempotency ?? {}),
            processedKeys: base.idempotency?.processedKeys ?? DEFAULT_STATE.idempotency.processedKeys,
        },
        missionClaims: {
            ...DEFAULT_STATE.missionClaims,
            ...(base.missionClaims ?? {}),
            dailyAuto: base.missionClaims?.dailyAuto ?? DEFAULT_STATE.missionClaims.dailyAuto,
            weeklyManual: base.missionClaims?.weeklyManual ?? DEFAULT_STATE.missionClaims.weeklyManual,
            monthlyManual: base.missionClaims?.monthlyManual ?? DEFAULT_STATE.missionClaims.monthlyManual,
        },
        pass: {
            ...DEFAULT_STATE.pass,
            ...(base.pass ?? {}),
            claimedRewardIds: base.pass?.claimedRewardIds ?? DEFAULT_STATE.pass.claimedRewardIds,
        },
        pet: {
            ...DEFAULT_STATE.pet,
            ...(base.pet ?? {}),
        },
        boss: {
            ...DEFAULT_STATE.boss,
            ...(base.boss ?? {}),
            participants: base.boss?.participants ?? DEFAULT_STATE.boss.participants,
            contributions: base.boss?.contributions ?? DEFAULT_STATE.boss.contributions,
            dailyAppliedByUser: base.boss?.dailyAppliedByUser ?? DEFAULT_STATE.boss.dailyAppliedByUser,
        },
        inventory: {
            ...DEFAULT_STATE.inventory,
            ...(base.inventory ?? {}),
            petItems: base.inventory?.petItems ?? DEFAULT_STATE.inventory.petItems,
            cosmetics: base.inventory?.cosmetics ?? DEFAULT_STATE.inventory.cosmetics,
            tickets: base.inventory?.tickets ?? DEFAULT_STATE.inventory.tickets,
        },
        monthlySocialProgress: {
            ...DEFAULT_STATE.monthlySocialProgress,
            ...(base.monthlySocialProgress ?? {}),
        },
        kaizenReview: {
            ...DEFAULT_STATE.kaizenReview,
            ...(base.kaizenReview ?? {}),
        },
        notificationPreferences: {
            ...DEFAULT_STATE.notificationPreferences,
            ...(base.notificationPreferences ?? {}),
        },
        accounts: base.accounts ?? DEFAULT_STATE.accounts,
        socialProfiles: base.socialProfiles ?? DEFAULT_STATE.socialProfiles,
        habits: base.habits ?? DEFAULT_STATE.habits,
        tasks: base.tasks ?? DEFAULT_STATE.tasks,
        projects: base.projects ?? DEFAULT_STATE.projects,
        goals: base.goals ?? DEFAULT_STATE.goals,
        challenges: base.challenges ?? DEFAULT_STATE.challenges,
        duels: base.duels ?? DEFAULT_STATE.duels,
        invites: base.invites ?? DEFAULT_STATE.invites,
        notifications: base.notifications ?? DEFAULT_STATE.notifications,
        notificationsUnreadCount: base.notificationsUnreadCount ?? DEFAULT_STATE.notificationsUnreadCount,
        notificationsTotal: base.notificationsTotal ?? DEFAULT_STATE.notificationsTotal,
        notificationsPreviewLimit: base.notificationsPreviewLimit ?? DEFAULT_STATE.notificationsPreviewLimit,
        friendRequests: base.friendRequests ?? DEFAULT_STATE.friendRequests,
        friendships: base.friendships ?? DEFAULT_STATE.friendships,
        blockedUsers: base.blockedUsers ?? DEFAULT_STATE.blockedUsers,
        leagueBoard: base.leagueBoard ?? DEFAULT_STATE.leagueBoard,
        subscriptionHistory: base.subscriptionHistory ?? DEFAULT_STATE.subscriptionHistory,
        ownedEmporiumItemIds: base.ownedEmporiumItemIds ?? DEFAULT_STATE.ownedEmporiumItemIds,
        xpHistory: base.xpHistory ?? DEFAULT_STATE.xpHistory,
        dashboardOverview: base.dashboardOverview ?? DEFAULT_STATE.dashboardOverview,
        socialCache: base.socialCache ?? DEFAULT_STATE.socialCache,
    }
}

export function normalizeEnabledModules(value: unknown): EnabledModules {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return DEFAULT_STATE.enabledModules
    }

    const source = value as Record<string, unknown>
    return {
        core: true,
        gamificacao: Boolean(source.gamificacao ?? DEFAULT_STATE.enabledModules.gamificacao),
        tarefas: Boolean(source.tarefas ?? DEFAULT_STATE.enabledModules.tarefas),
        arena: Boolean(source.arena ?? DEFAULT_STATE.enabledModules.arena),
    }
}

export function normalizeNotificationPreferences(value: unknown): NotificationPreferences {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return DEFAULT_STATE.notificationPreferences
    }

    const source = value as Record<string, unknown>
    return {
        enabled: Boolean(source.enabled ?? DEFAULT_STATE.notificationPreferences.enabled),
        streakRisk: Boolean(source.streakRisk ?? DEFAULT_STATE.notificationPreferences.streakRisk),
        missions: Boolean(source.missions ?? DEFAULT_STATE.notificationPreferences.missions),
        boss: Boolean(source.boss ?? DEFAULT_STATE.notificationPreferences.boss),
        levelUp: Boolean(source.levelUp ?? DEFAULT_STATE.notificationPreferences.levelUp),
        social: Boolean(source.social ?? DEFAULT_STATE.notificationPreferences.social),
        system: Boolean(source.system ?? DEFAULT_STATE.notificationPreferences.system),
        soundFeedback: Boolean(source.soundFeedback ?? DEFAULT_STATE.notificationPreferences.soundFeedback),
        kaizen: Boolean(source.kaizen ?? DEFAULT_STATE.notificationPreferences.kaizen),
    }
}

export function normalizeAccessibility(value: unknown): AccessibilitySettings {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return DEFAULT_STATE.accessibility
    }

    const source = value as Record<string, unknown>
    const fontScale = source.fontScale === 'grande' || source.fontScale === 'large' ? 'grande' : 'normal'
    return {
        reduceMotion: Boolean(source.reduceMotion ?? DEFAULT_STATE.accessibility.reduceMotion),
        highContrast: Boolean(source.highContrast ?? DEFAULT_STATE.accessibility.highContrast),
        fontScale,
    }
}

export function normalizeLinkedHabitIds(value: unknown) {
    if (!Array.isArray(value)) return []
    return value.filter((entry): entry is string => typeof entry === 'string')
}

export function normalizeTaskPriority(value: unknown): 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' {
    if (value === 'LOW' || value === 'HIGH' || value === 'URGENT') return value
    // Backwards compat: map legacy Portuguese values
    if (value === 'baixa') return 'LOW'
    if (value === 'alta') return 'HIGH'
    if (value === 'urgente') return 'URGENT'
    if (value === 'media') return 'MEDIUM'
    return 'MEDIUM'
}
